import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Typography, Box, CircularProgress, IconButton, InputAdornment, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import {
  Search as SearchIcon, Close as CloseIcon,
  Gavel as GavelIcon
} from '@mui/icons-material';
import { useQuery } from '@apollo/client';
import { GET_ALL_VAT_EXEMPTION_REASONS } from '../graphql/queries';

// Category translations and colors
const CATEGORY_LABELS = {
  'G': 'Износ',
  'K': 'ВОД',
  'E': 'Освободена',
  'O': 'Извън обхвата',
  'Z': 'Нулева ставка',
  'AE': 'Обратно начисляване',
  'S': 'Стандартна'
};

const CATEGORY_COLORS = {
  'G': 'success',
  'K': 'info',
  'E': 'warning',
  'O': 'default',
  'Z': 'primary',
  'AE': 'secondary',
  'S': 'default'
};

export const VatExemptionSearchModal = ({
  open,
  onClose,
  onReasonSelected,
  currentReasonId
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');

  const { data, loading } = useQuery(GET_ALL_VAT_EXEMPTION_REASONS);

  // Clear search when modal closes
  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setSelectedCategory('ALL');
    }
  }, [open]);

  // Filter and sort reasons
  const filteredReasons = useMemo(() => {
    if (!data?.allVatExemptionReasons) return [];

    let reasons = data.allVatExemptionReasons
      .filter(reason => reason.isActive)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      reasons = reasons.filter(reason =>
        reason.reasonName.toLowerCase().includes(term) ||
        (reason.reasonNameEn && reason.reasonNameEn.toLowerCase().includes(term)) ||
        reason.reasonCode.toLowerCase().includes(term) ||
        reason.legalBasis.toLowerCase().includes(term) ||
        (reason.ublExemptionCode && reason.ublExemptionCode.toLowerCase().includes(term)) ||
        (reason.ublCategoryCode && reason.ublCategoryCode.toLowerCase().includes(term))
      );
    }

    // Filter by category
    if (selectedCategory !== 'ALL') {
      reasons = reasons.filter(reason => reason.ublCategoryCode === selectedCategory);
    }

    return reasons;
  }, [data, searchTerm, selectedCategory]);

  // Get unique categories from data
  const categories = useMemo(() => {
    if (!data?.allVatExemptionReasons) return [];
    const uniqueCategories = [...new Set(
      data.allVatExemptionReasons
        .filter(r => r.isActive && r.ublCategoryCode)
        .map(r => r.ublCategoryCode)
    )];
    return uniqueCategories.sort();
  }, [data]);

  const handleReasonSelect = (reason) => {
    onReasonSelected(reason);
    onClose();
    setSearchTerm('');
  };

  const handleClose = () => {
    onClose();
    setSearchTerm('');
    setSelectedCategory('ALL');
  };

  // Find current reason name
  const currentReason = useMemo(() => {
    if (!currentReasonId || !data?.allVatExemptionReasons) return null;
    return data.allVatExemptionReasons.find(r => r.id === currentReasonId);
  }, [currentReasonId, data]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px', maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GavelIcon color="primary" />
            <Typography variant="h6">
              Основания за неначисляване на ДДС
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Търсете по код, име на български или английски, или правно основание
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Search field */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Търсене... (напр. 'износ', 'чл. 28', 'VATEX-EU-G')"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Category filter chips */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label="Всички"
            color={selectedCategory === 'ALL' ? 'primary' : 'default'}
            onClick={() => setSelectedCategory('ALL')}
            variant={selectedCategory === 'ALL' ? 'filled' : 'outlined'}
          />
          {categories.map(cat => (
            <Chip
              key={cat}
              label={`${CATEGORY_LABELS[cat] || cat} (${cat})`}
              color={selectedCategory === cat ? (CATEGORY_COLORS[cat] || 'primary') : 'default'}
              onClick={() => setSelectedCategory(cat)}
              variant={selectedCategory === cat ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
        </Box>

        {/* Results table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: '400px' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: '130px' }}>UBL Код</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '80px' }}>Категория</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Основание (БГ)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Reason (EN)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Правно основание</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReasons.length > 0 ? (
                  filteredReasons.map((reason) => (
                    <TableRow
                      key={reason.id}
                      hover
                      selected={currentReasonId === reason.id}
                      onClick={() => handleReasonSelect(reason)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'primary.50'
                        },
                        ...(currentReasonId === reason.id && {
                          backgroundColor: 'primary.100'
                        })
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                          {reason.ublExemptionCode || reason.reasonCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={reason.ublCategoryCode || '-'}
                          size="small"
                          color={CATEGORY_COLORS[reason.ublCategoryCode] || 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {reason.reasonName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {reason.reasonNameEn || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {reason.legalBasis}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        {searchTerm
                          ? `Няма намерени основания за "${searchTerm}"`
                          : 'Няма налични основания за неначисляване на ДДС'
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Info box */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>UBL VATEX кодове</strong> - Стандартни кодове за освобождаване от ДДС, използвани в електронното фактуриране съгласно директивите на ЕС.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {currentReason && `Текущо: ${currentReason.reasonName}`}
        </Typography>
        <Button onClick={handleClose} color="secondary">
          Отказ
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VatExemptionSearchModal;
